// Brand tokens for JS-styled components + Recharts. These READ the CSS custom
// properties (solvigo-tokens → theme-solvigo → theme-minprilla overlay), so the
// active theme/tenant flows into charts and inline styles WITHOUT touching a
// component. Read once at module load — CSS is imported first in main.tsx and
// <html data-theme> is set in index.html, so the cascade is resolved by now.
// Fallbacks are the Solvigo defaults.

import { formatAxisCompact, formatQuantity } from './format'
import { TENANT } from './tenant'

function cssVar(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return v || fallback
}

export const brand = {
  teal: cssVar('--brand-primary', '#f54e00'), // accent: orange (default) / teal (minprilla)
  tealDark: cssVar('--brand-dark', '#1a2e46'), // steel-blue / petrol
  ink: cssVar('--ink', '#000000'),
  text: cssVar('--ink', '#000000'),
  mint: cssVar('--brand-tint', '#e1e1de'),
  grey: cssVar('--line', '#e1e1de'),
  greyText: cssVar('--muted', '#4a4a4a'),
  surface: cssVar('--surface', '#ffffff'),
  accent: cssVar('--accent', '#f54e00'),
  seriesMuted: cssVar('--series-muted', '#c9c9c5'),
  font: cssVar('--font-body', "'Jost', 'Futura', Arial, sans-serif"),
  fontDisplay: cssVar('--font-display', "'Jost', 'Futura', Arial, sans-serif"),
} as const

// Data-viz series colours: chart-1 = the tenant accent, chart-2..7 a CATEGORICAL palette of
// DISTINCT hues (not a lightness ramp of one colour — the old petrol/black/grey were
// indistinguishable, Olof 31/8), chosen for colour-vision-deficiency separation and legible on
// both light and dark card grounds. The LAST entry is the neutral "others" tone (seriesStyles
// treats palette[length-1] as neutral), so keep it last.
export const dataPalette = [
  cssVar('--chart-1', '#2bb5b8'), // accent (teal / orange per tenant)
  cssVar('--chart-2', '#0072b2'), // blue
  cssVar('--chart-3', '#d55e00'), // vermillion
  cssVar('--chart-4', '#cc79a7'), // reddish purple
  cssVar('--chart-5', '#009e73'), // bluish green
  cssVar('--chart-6', '#e69f00'), // amber
  cssVar('--chart-7', '#8a5cd0'), // violet
  cssVar('--chart-8', '#9aa0a6'), // neutral grey — the "others" baseline (must stay last)
] as const

export const series = dataPalette

// Canonical, brand-aware series colour — the SAME brand gets the SAME colour on
// every chart (rule: samma data, samma färg, överallt). Each of the tenant's own
// brands gets its OWN saturated colour (chart-1 → chart-3, fixed by its position in
// this list, so it never shifts between charts); every other brand → chart-4 (a
// single neutral baseline) so the own series pop. "Övriga" (donut tail) → the muted
// grey. No hashing, no arbitrary mix.
export const OWN_BRANDS: readonly string[] = [
  ...new Set([TENANT.brandHighlight, ...TENANT.ownBrands].filter(Boolean).map((s) => s.toUpperCase())),
]

export function brandColor(name: string): string {
  const n = String(name).toUpperCase()
  if (n === 'ÖVRIGA') return brand.seriesMuted
  const saturated = dataPalette.slice(0, -1) // every distinct hue (all but the neutral tail)
  const own = OWN_BRANDS.indexOf(n)
  if (own >= 0) return saturated[own % saturated.length] // one distinct colour per own brand
  return dataPalette[dataPalette.length - 1] // the neutral baseline for every other brand
}

// Single-metric category ranking (city, payment, time): one colour for the whole
// series — the metric's own accent. A ranking is not a set of categories to tell
// apart by hue; position + label already do that.
export const metricColor = dataPalette[0]

export const axisTick = {
  fill: brand.text,
  fontSize: 12,
  fontFamily: brand.font,
} as const

export const gridStroke = cssVar('--grid', '#e1e1de')

// One shared number formatter for axes, labels and tooltips.
// English chrome (A1): thin-space thousands and a DOT decimal separator. The app used to
// mix sv-SE grouping+comma on axes ("3,5 k") with dot decimals in cards ("15.4 %"). No
// locale gives space-grouping with a dot decimal, so format en-GB and swap its comma
// groupers for a NARROW NO-BREAK SPACE (U+202F) — the separator the rest of the stack
// already uses, including the grounding check's number parser (contract.py). A plain thin
// space (U+2009) would have made "60 937" parse as 937 there, silently turning a magnitude
// into a small structural number.
// MP-OS-191: the same quantity format the rest of the standard uses (format.ts).
export const formatNumber = formatQuantity
// Compact AXIS ticks (labels/tooltips keep the full number via formatNumber). A 7-digit value
// like 1 050 000 overflows the narrow Y-axis gutter, so ticks scale: "1.05 M", "512.3 k".
// MP-OS-191: the one compact format (format.ts) on every chart, never "1 050 k".
// One argument only, so it is safe as a Recharts tickFormatter(value, index) (MPD-46 #1).
export const axisTickFormatter = (value: number | string): string => formatAxisCompact(value)
export function formatValue(v: number, unit?: string): string {
  const n = formatNumber(v)
  if (!unit) return n
  return unit.includes('%') ? `${n} %` : `${n} ${unit}`
}
